import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';

import { FieldType } from 'constants/formGenerator';
import { setIn } from 'utils/data';
import { checkIsFormChanged, isBooleanToggleField } from 'utils/form';
import { jsonParse } from 'utils/json';
import { formatDatePendingChange } from 'utils/pendingChanges';

import useValidation from './useValidation';

const useForm = (initialState, fieldsConfig, observableFieldsNames = []) => {
  const [isTouched, setIsTouched] = useState(false);
  const [currentForm, setCurrentForm] = useState(initialState);
  const [initialForm, setInitialForm] = useState(initialState);

  const formRef = useRef(initialState);
  // Fields the user changed themselves, as opposed to values the form's effects derived from those edits.
  const userEditedFieldNamesRef = useRef(new Set());

  useEffect(() => {
    formRef.current = currentForm;
  }, [currentForm]);

  const {
    validationErrors,
    validationErrorsRef,
    setSchema,
    validate,
    validateField,
    staticValidateField,
    setValidationErrors,
    setExcludedValidationPaths,
  } = useValidation(formRef);

  const isFormChanged = useMemo(
    () => isTouched && checkIsFormChanged({ initialForm, currentForm, fieldsConfig }),
    [isTouched, initialForm, currentForm, fieldsConfig],
  );

  const formPendingChanges = useMemo(
    () =>
      observableFieldsNames.reduce((acc, fieldName) => {
        const initialValue = _.get(initialForm, fieldName);
        const newValue = _.get(currentForm, fieldName);
        const { fieldType, multiple, format, withTime } = _.get(fieldsConfig, fieldName) ?? {};
        const shouldExtractValue = fieldType === FieldType.SELECT;
        const isBooleanToggle = isBooleanToggleField(fieldType);
        const isDate = fieldType === FieldType.DATE;

        const formatPendingValues = () => {
          if (shouldExtractValue) {
            const extractValue = valueToExtract =>
              (multiple && valueToExtract?.map(({ value }) => value)) ||
              (valueToExtract && valueToExtract?.value);

            return { newValue: extractValue(newValue), initialValue: extractValue(initialValue) };
          }

          if (isBooleanToggle) {
            return {
              newValue: newValue ?? false,
              initialValue: initialValue ?? false,
            };
          }

          if (isDate) {
            return formatDatePendingChange({ newValue, initialValue, format, withTime });
          }

          return { newValue, initialValue };
        };

        _.set(acc, fieldName, formatPendingValues());

        return acc;
      }, {}),
    [initialForm, currentForm, observableFieldsNames, fieldsConfig],
  );

  const setInForm = useCallback((name, value, isInitiatedByUser = true) => {
    if (isInitiatedByUser) {
      setIsTouched(true);
      userEditedFieldNamesRef.current.add(name);
    }

    setCurrentForm(form => setIn(form, name, value));
  }, []);

  const setMultipleInForm = useCallback((changes, isInitiatedByUser = true) => {
    if (isInitiatedByUser) {
      setIsTouched(true);
      Object.keys(changes).forEach(name => userEditedFieldNamesRef.current.add(name));
    }

    setCurrentForm(form =>
      Object.entries(changes).reduce(
        (acc, [name, value]) => setIn(acc, name, value),
        _.cloneDeep(form),
      ),
    );
  }, []);

  const resetForm = useCallback(() => {
    setCurrentForm(initialForm || initialState);
    userEditedFieldNamesRef.current = new Set();
    setValidationErrors({});
  }, [initialForm, initialState, setValidationErrors]);

  const handleChange = useCallback(
    async (e, validationContext) => {
      const { name, type, checked, value, dataset } = e.target;

      const isNoBoolean = dataset?.noboolean;
      const isCheckbox = type === 'checkbox';
      const isStringBoolean = ['true', 'false'].includes(value);
      const formattedStringBooleanValue =
        isStringBoolean && !isNoBoolean ? jsonParse(value) : value;

      const newValue = isCheckbox ? checked : formattedStringBooleanValue;

      setIsTouched(true);
      userEditedFieldNamesRef.current.add(name);

      setCurrentForm(form => _.set(_.cloneDeep(form), name, newValue));

      if (!dataset?.novalidate) {
        await validateField(name, newValue, validationContext || formRef.current);
      }
    },
    [validateField],
  );

  const validateForm = useCallback(
    async context => {
      const noFormChangedValidate = !!context?.noFormChangedValidate;

      if (
        (!isFormChanged && !noFormChangedValidate) ||
        !(await validate(currentForm, { context: { ...context, ...formRef.current } }))
      ) {
        return false;
      }

      return true;
    },
    [currentForm, isFormChanged, validate],
  );

  return {
    form: currentForm,
    initialForm,
    formRef,
    userEditedFieldNamesRef,
    setForm: setCurrentForm,
    setInitialFormValues: setInitialForm,
    isFormChanged,
    validationErrors,
    validationErrorsRef,
    handleChange,
    validateForm,
    resetForm,
    setIsTouched,
    setSchema,
    setInForm,
    setMultipleInForm,
    validateField,
    staticValidateField,
    setValidationErrors,
    setExcludedValidationPaths,
    formPendingChanges,
  };
};

export default useForm;
